import { faHourglassStart } from "@fortawesome/pro-regular-svg-icons/faHourglassStart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Stack, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImages } from "app/appImages";
import { MprcLink } from "components";
import { ChannelAutoRegisterMode, FeedChannelHeaderResponse, ManagedChannelMembersListOrderBy, UserItemDtoPaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";
import { ComplexInfoHeader } from "views/knowledge/components/ComplexInfoHeader";
import { ActionChannelButton, PendingApprovalChannelButton, ShareChannelButton, SubscribeChannelButton, ToggleMutedChannelButton, UnsubscribeChannelButton } from "views/knowledge/feed/FeedChannel/defaults";

interface IProps {
    channel: FeedChannelHeaderResponse | null;
    disableGoback?: boolean;
    onReset: () => void;
    onRigthPanelButtonClick?: () => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Cabeçalho do Feed de Canais
 * @param props
 */
export const FeedChannelHeader: React.FC<IProps> = (props) => {
    const channel = props.channel!;
    const { disableGoback, onReset, onRigthPanelButtonClick } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [openMembersDrawer, setOpenMembersDrawer] = useState<boolean>(false);

    const subtitle = useMemo(() => {
        const items: React.ReactNode[] = [];
        items.push(t("shared:SysObjects.Channel"));
        items.push(
            <MprcLink onClick={() => setOpenMembersDrawer(true)}>
                <Typography variant="caption" color="white" fontWeight="bold">
                    {t("Feed.Channel.TotalSubscribersText", { count: channel.totalSubscribers })}
                </Typography>
            </MprcLink>
        );
        return items;
    }, [channel.totalSubscribers]);

    const subscribersDrawer =
        openMembersDrawer && <ListUsersDrawer
            title={t("Feed.Channel.SubscribersDrawerTitle")}
            subtitle={channel.name}
            onGetData={({ searchText, pageNumber, pageSize }) =>
                new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                    api.v1.channelManagement.getMembers({
                        ChannelId: channel.channelId,
                        ExpandedMode: true,
                        SearchText: searchText,
                        OrderBy: ManagedChannelMembersListOrderBy.Name,
                        PageNumber: pageNumber,
                        PageSize: pageSize
                    })
                        .then(({ data }) => resolve({
                            items: data.items.map(item => ({ userId: item.memberId, name: item.name, thumbnailUrl: item.thumbnailUrl })),
                            pageNumber: data.pageNumber,
                            pageSize: data.pageSize,
                            totalPages: data.totalPages,
                            totalRows: data.totalRows
                        }))
                        .catch(e => reject(e));
                })}
            onClose={() => setOpenMembersDrawer(false)}
        />;


    return (
        <>
            {subscribersDrawer}
            <ComplexInfoHeader
                title={channel.name}
                subtitle={subtitle}
                description={channel.description}
                thumbnailUrl={channel.thumbnailUrl}
                disableGoback={disableGoback}
                defaultThumbnail={AppImages.KnowledgeChannelThumbnail}
                onRigthPanelButtonClick={onRigthPanelButtonClick}
                actions={
                    <Grid container alignItems="center">
                        <Grid>
                            <ShareChannelButton channel={channel} />
                        </Grid>
                        <Grid>
                            <ActionChannelButton channel={channel} />
                        </Grid>
                    </Grid>
                }
                buttons={
                    <Stack direction="row" alignItems="center" gap={1} width="100%" sx={{ pr: 2, pl: 2 }}>
                        {
                            channel.registered
                                ?
                                <>
                                    <UnsubscribeChannelButton
                                        channel={channel}
                                        isOwnerChannel={channel.isOwnerChannel}
                                        onComplete={() => onReset()}
                                    />
                                    {
                                        channel.pendingSubscription ?
                                            <FontAwesomeIcon
                                                className={AppGlobalClassKeys.clickable}
                                                icon={faHourglassStart}
                                                title={t("Subcriptions.Signup.WaitingProcessingTooltip")}
                                            />
                                            :
                                            <ToggleMutedChannelButton
                                                channel={props.channel}
                                                onSuccess={() => onReset()}
                                            />
                                    }
                                </>
                                : channel.pendingApproval
                                    ? <PendingApprovalChannelButton />
                                    : channel.autoRegisterMode != ChannelAutoRegisterMode.Forbidden &&
                                    <SubscribeChannelButton
                                        channel={channel}
                                        onComplete={() => onReset()}
                                    />
                        }
                    </Stack>
                }
            />
        </>
    );
};
